// client/src/components/pages/Geofencing/zoneEditor/ZoneImportDialog.tsx
// Import a zone shape (ST-00239): paste or upload GeoJSON / KML, or paste a
// list of "lat, lng" corners; when the source holds several shapes the user
// picks one. Parsing is zoneFormat.parseZoneImport (pure, unit-tested).
import { useEffect, useMemo, useRef, useState } from 'react';
import { FileUp, X } from 'lucide-react';
import { parseZoneImport, type ImportCandidate, type ImportResult } from './zoneFormat';
import {
  MAX_VERTICES,
  formatArea,
  formatLength,
  shapeAreaM2,
  simplifyToMax,
  type ZoneShape,
} from './zoneGeometry';

export interface ZoneImportDialogProps {
  open: boolean;
  onClose: () => void;
  /** The chosen shape (already simplified when it had too many corners) and its name. */
  onImport: (shape: ZoneShape, name: string, notes: string[]) => void;
}

const MAX_FILE_BYTES = 5 * 1024 * 1024;

function describe(c: ImportCandidate): string {
  const area = formatArea(shapeAreaM2(c.shape));
  return c.shape.kind === 'circle'
    ? `Circle, radius ${formatLength(c.shape.radiusM)} · ${area}`
    : `${c.shape.ring.length} corners · ${area}`;
}

export default function ZoneImportDialog({ open, onClose, onImport }: ZoneImportDialogProps) {
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!open) return;
    setText('');
    setFileName(null);
    setResult(null);
    setPicked(null);
    setFileError(null);
    window.setTimeout(() => textRef.current?.focus(), 0);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  const read = (value: string): void => {
    const r = parseZoneImport(value);
    setResult(r);
    setPicked(r.candidates.length === 1 ? r.candidates[0]!.id : null);
  };

  // Read pasted text as soon as typing pauses (no extra button to find).
  useEffect(() => {
    if (!open || !text.trim() || result) return undefined;
    const t = window.setTimeout(() => read(text), 350);
    return () => window.clearTimeout(t);
  }, [open, text, result]);

  const onFile = (file: File | undefined): void => {
    setFileError(null);
    if (!file) return;
    if (/\.kmz$/i.test(file.name)) {
      setFileError(
        'KMZ files are zipped. Open it in Google Earth and save it as KML, then try again.'
      );
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setFileError('That file is larger than 5 MB. Export only the zone you need.');
      return;
    }
    setFileName(file.name);
    file
      .text()
      .then((t) => {
        setText(t);
        read(t);
      })
      .catch(() => setFileError('The file could not be read.'));
  };

  const chosen = useMemo(
    () => result?.candidates.find((c) => c.id === picked) ?? null,
    [result, picked]
  );

  if (!open) return null;

  const apply = (): void => {
    if (!chosen) return;
    let shape = chosen.shape;
    const notes = [...chosen.notes];
    if (shape.kind === 'polygon' && shape.ring.length > MAX_VERTICES) {
      const ring = simplifyToMax(shape.ring, MAX_VERTICES);
      notes.push(`Simplified from ${shape.ring.length} to ${ring.length} corners.`);
      shape = { kind: 'polygon', ring };
    }
    onImport(shape, chosen.name, notes);
  };

  return (
    <div
      className="fixed inset-0 z-[1200] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ze-import-title"
        data-ze-modal
        className="flex max-h-[90dvh] w-full flex-col rounded-t-xl border border-gray-200 bg-surface shadow-2xl sm:max-w-xl sm:rounded-xl"
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <h3 id="ze-import-title" className="text-base font-semibold text-gray-900">
            Import a zone shape
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1.5 text-gray-500 hover:bg-gray-100"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <div className="space-y-3 overflow-y-auto px-4 py-3">
          <p className="text-sm text-gray-600">
            Paste GeoJSON or KML (for example from Google Earth or geojson.io), or one{' '}
            <span className="whitespace-nowrap">"latitude, longitude"</span> corner per line. Or
            choose a file.
          </p>

          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-gray-300 bg-surface px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus-within:ring-2 focus-within:ring-teal-500">
            <FileUp size={16} aria-hidden="true" />
            Choose a file (.geojson, .json, .kml)
            <input
              type="file"
              accept=".geojson,.json,.kml,application/geo+json,application/json,application/vnd.google-earth.kml+xml"
              className="sr-only"
              onChange={(e) => onFile(e.target.files?.[0])}
            />
          </label>
          {fileName && <p className="text-xs text-gray-500">Read {fileName}</p>}
          {fileError && (
            <p role="alert" className="text-sm text-red-600">
              {fileError}
            </p>
          )}

          <div>
            <label
              htmlFor="ze-import-text"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Or paste here
            </label>
            <textarea
              id="ze-import-text"
              ref={textRef}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                setResult(null);
                setPicked(null);
              }}
              rows={6}
              spellCheck={false}
              placeholder={'{"type": "Feature", "geometry": {"type": "Polygon", ...}}'}
              className="w-full rounded-lg border border-gray-300 bg-surface px-3 py-2 font-mono text-xs text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>

          {result?.error && (
            <p role="alert" className="text-sm text-red-600">
              {result.error}
            </p>
          )}

          {result && result.candidates.length > 0 && (
            <fieldset>
              <legend className="mb-1 text-sm font-medium text-gray-700">
                {result.candidates.length === 1
                  ? 'Found 1 shape'
                  : `Found ${result.candidates.length} shapes. Pick the one for this zone`}
              </legend>
              <ul className="max-h-56 space-y-1 overflow-y-auto">
                {result.candidates.map((c) => (
                  <li key={c.id}>
                    <label
                      className={`flex cursor-pointer items-start gap-2 rounded-lg border px-3 py-2 text-sm ${
                        picked === c.id
                          ? 'border-teal-500 bg-teal-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="ze-import-pick"
                        checked={picked === c.id}
                        onChange={() => setPicked(c.id)}
                        className="mt-0.5 h-4 w-4 border-gray-300 text-teal-600 focus:ring-teal-500"
                      />
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-gray-900">{c.name}</span>
                        <span className="block text-xs text-gray-500">{describe(c)}</span>
                        {c.notes.map((n) => (
                          <span key={n} className="block text-xs text-amber-700">
                            {n}
                          </span>
                        ))}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </fieldset>
          )}

          {result && result.skipped.length > 0 && (
            <details className="text-xs text-gray-500">
              <summary className="cursor-pointer">
                {result.skipped.length} item{result.skipped.length === 1 ? '' : 's'} skipped
              </summary>
              <ul className="mt-1 list-disc pl-5">
                {result.skipped.slice(0, 20).map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            </details>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 bg-surface px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={apply}
            disabled={!chosen}
            className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
          >
            Use this shape
          </button>
        </div>
      </div>
    </div>
  );
}
